import React, { useEffect, useState } from 'react';
import '../styles/Burning.css';
import { ethers } from 'ethers';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  ScatterChart, Scatter,  
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

const burnManagerAddress = "0x7231CC09c3A4236163E6BC05c1A848ACE397408B";

const abi = [
  "function getTotalBurned() view returns (uint256)",
  "function getAllPools() view returns (string[] memory)",
  "function getPoolInfo(string calldata pool) view returns (uint256 burned, uint256 allocation, uint256 percentage, address wallet)",
  "function getBurnEvents() view returns (tuple(uint256 amount, uint256 timestamp, string pool, string reason)[] memory)"
];

const POOL_COLORS = [
  '#ef4444',  // red dark
  '#60a5fa', // blue
  '#fbbf24', // amber
  '#f87171', // red
  '#34d399', // emerald
  '#a78bfa', // violet
  '#8b5cf6', // purple
  '#10b981', // emerald dark
  '#f59e0b', // yellow
  '#818cf8', // indigo
  '#ec4899', // fuchsia
  '#f472b6', // pink
  '#2dd4bf', // teal
  '#fb923c', // orange
  '#4ade80', // green
  '#38bdf8', // light blue
];
const PIE_COLORS = ['#4ade80', '#ef4444'];

const BurningDashboard = () => {
  const [totalBurned, setTotalBurned] = useState(0);
  const [poolData, setPoolData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [currentMonthBurn, setCurrentMonthBurn] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const totalGoal = 30000000000; // 30 Billion MCN

  const numberFormatter = (num) => num.toLocaleString();

  useEffect(() => {
    const fetchBurnData = async () => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const contract = new ethers.Contract(burnManagerAddress, abi, provider);

        const [totalBurnedRaw, poolNames, eventData] = await Promise.all([
          contract.getTotalBurned(),
          contract.getAllPools(),
          contract.getBurnEvents()
        ]);

        const totalBurnedFormatted = Number(ethers.utils.formatUnits(totalBurnedRaw, 18));
        setTotalBurned(totalBurnedFormatted);

        const pools = await Promise.all(poolNames.map(async (name) => {
          const info = await contract.getPoolInfo(name);
          return {
            name,
            burned: Number(ethers.utils.formatUnits(info.burned, 18)),
            allocation: Number(ethers.utils.formatUnits(info.allocation, 18))
          };
        }));

        setPoolData(pools);

        const burnEvents = eventData.map((event) => ({
          burned: Number(ethers.utils.formatUnits(event.amount, 18)),
          date: new Date(Number(event.timestamp) * 1000),
          pool: event.pool,
          reason: event.reason
        }));

        const monthlyBurn = {};
        let currentMonthTotal = 0;
        const now = new Date();

        burnEvents.forEach(({ burned, date }) => {
          const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
          monthlyBurn[key] = (monthlyBurn[key] || 0) + burned;

          if (date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()) {
            currentMonthTotal += burned;
          }
        });

        const monthlyArray = Object.entries(monthlyBurn).map(([key, burned]) => ({
          month: key,
          burned
        })).sort((a, b) => new Date(a.month) - new Date(b.month));

        setMonthlyData(monthlyArray);
        setCurrentMonthBurn(currentMonthTotal);
        setLoading(false);
      } catch (err) {
        console.error("Error loading burn data", err);
        setError(true);
        setLoading(false);
      }
    };

    fetchBurnData();
  }, []);

  const pieData = [
    { name: 'Burned', value: totalBurned },
    { name: 'Remaining', value: totalGoal - totalBurned }
  ];

  const progress = ((totalBurned / totalGoal) * 100).toFixed(2);

  const topPools = [...poolData].sort((a, b) => b.burned - a.burned).slice(0, 3);

  if (loading) {
    return (
      <div className="burning-dashboard">
        <div className="spinner"></div>
        <p>Loading burning data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="burning-dashboard">
        <p className="error-message">⚠️ Failed to load burning data. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="burning-dashboard">
      <h1 className="dashboard-title">🔥 MediCoin Burning Dashboard</h1>
      <p className="burning-intro">
      MediCoin follows a long-term deflationary model by systematically burning 30 billion tokens over 36 months. Tokens are retired from multiple allocation pools, ensuring transparent reduction in supply while increasing scarcity and long-term holder value.
</p>

      <div className="burning-stats">
        <div className="burning-card"><h2>Total Burned</h2><p>{numberFormatter(totalBurned)} MCN</p></div>
        <div className="burning-card"><h2>Burn Goal (3 Years)</h2><p>{numberFormatter(totalGoal)} MCN</p></div>
        <div className="burning-card">
          <h2>Progress</h2>
          <p>{progress}%</p>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="burning-card"><h2>Burned This Month</h2><p>{numberFormatter(currentMonthBurn)} MCN</p></div>
      </div>

      <div className="burning-charts-grid">
        <div className="burning-chart">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="burning-chart">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={poolData} margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={numberFormatter} />
              <Tooltip />
              <Legend />
              <Bar dataKey="burned">
                {poolData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={POOL_COLORS[index % POOL_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="burning-chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData} margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={numberFormatter} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="burned" stroke="#38bdf8" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="burning-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
              <CartesianGrid />
              <XAxis type="category" dataKey="name" />
              <YAxis dataKey="burned" tickFormatter={numberFormatter} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="Burn Progress" data={poolData} fill="#34d399">
                {poolData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={POOL_COLORS[index % POOL_COLORS.length]} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

      </div>

      <h3 className="burn-table-title">🔥 Burn Pools Overview</h3>
      <div className="burning-table">
        <table>
          <thead>
            <tr>
              <th>Pool</th>
              <th>Burned</th>
              <th>Allocation</th>
              <th>% Burned</th>
            </tr>
          </thead>
          <tbody>
            {poolData.map((pool, index) => (
              <tr key={index}>
                <td>{pool.name}</td>
                <td>{numberFormatter(pool.burned)}</td>
                <td>{numberFormatter(pool.allocation)}</td>
                <td>{((pool.burned / pool.allocation) * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="burn-table-title">🏆 Top 3 Burning Pools</h3>
      <ul className="top-pools">
        {topPools.map((pool, index) => (
          <li key={index}>
            <strong>{pool.name}</strong>: {numberFormatter(pool.burned)} MCN burned
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BurningDashboard;
